import { formatPhoneNumber } from "@/shared/utils/phone";

export function getStatusColor(
  status: string,
): "success" | "error" | "warning" | "info" | "primary" {
  const normalized = status.trim().toLowerCase();

  switch (normalized) {
    case "accepted":
    case "approved":
    case "completed":
      return "success";

    case "received":
      return 'primary';

    case "pending":
      return 'warning';
      
    case "booked":
    case "new":
      return "info";
    
    case "cancelled":
    case "cancel_request":
    case "rejected":
      return "error";
    
    default:
      return "primary";
      
  }
}
export function getTextColor(
  status: string,
): "text-success" | "text-error" | "text-warning" | "text-info" | "text-primary" {
  const normalized = status.trim().toLowerCase();

  switch (normalized) {
    case "accepted":
    case "active":
    case "approved":
    case "completed":
      return "text-success";

    case "received":
      return 'text-primary';

    case "pending":
      return 'text-warning';

    case "booked":
    case "new":
      return "text-info";
    
    case "cancelled":
    case "rejected":
      return "text-error";
    
    default:
      return "text-primary";
      
  }
}

export function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

export function formatPhone(value: string) {
  return formatPhoneNumber(value);
}

export function formatDate(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatWeekdayMonthDay(date: string | Date): string {
  let d: Date;

  if (typeof date === "string") {
    // Ensure plain YYYY-MM-DD stays in local time (avoid timezone shift)
    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      d = new Date(year, month - 1, day);
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }

  if (isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

export function formatTime(date: string | Date): string {
  let d: Date;
  if (typeof date === "string") {
    // Handle 24-hour time strings like '16:00' or '16:00:00'
    const match = date.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/);
    if (match) {
      const now = new Date();
      d = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        parseInt(match[1]),
        parseInt(match[2]),
        match[3] ? parseInt(match[3]) : 0,
      );
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function timeAgo(date: string | Date): string {
  const parsed = typeof date === "string" ? parseDateInput(date) : date;
  if (Number.isNaN(parsed.getTime())) {
    if (typeof date === "string") {
      const trimmed = date.trim();
      return trimmed || "-";
    }
    return "-";
  }

  const diffMs = Date.now() - parsed.getTime();
  if (diffMs < 0) return formatTime(parsed);

  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) return "just now";

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} d ago`;

  return formatDate(parsed);
}

function parseDateInput(date: string): Date {
  const trimmed = date.trim();
  if (!trimmed) return new Date("");

  const normalized = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
  return new Date(normalized);
}

export function formatPrice(amount: number) {
  if (typeof amount !== "number" || isNaN(amount)) return "-";
  return `$${amount.toFixed(0)}`;
}
